import type { PricingKey, PricingResponse } from '@medmax/api-client'

import { ClientConfig } from '@/lib/configs/ClientConfig'
import { RATINGS } from '@/lib/social-proof'

export type PriceTemplateValue = {
  total: string
  dollars: string
  cents: string
}

const templateNameByPricingKey = {
  MEDICAL_CERTIFICATE_SINGLE_DAY: 'medicalCertificateSingleDay',
  MEDICAL_CERTIFICATE_MULTI_DAY: 'medicalCertificateMultiDay',
  PRESCRIPTION: 'prescription',
  SPECIALIST_REFERRAL: 'specialistReferral',
  GENERAL_CONSULTATION: 'generalConsultation',
  TREATMENT_INITIAL_CONSULTATION: 'treatmentInitialConsultation',
  WEIGHT_LOSS_INITIAL_CONSULTATION: 'weightLossInitialConsultation',
  WEIGHT_LOSS_INITIAL_ASSESSMENT: 'weightLossInitialAssessment',
  WEIGHT_LOSS_MONTHLY_PLAN: 'weightLossMonthlyPlan',
} as const satisfies Record<PricingKey, string>

export type PricingTemplateName = (typeof templateNameByPricingKey)[PricingKey]

export type CmsTemplateData = {
  pricing: Record<PricingTemplateName, PriceTemplateValue>
  socialProof: {
    rating: string
    reviewCount: string
    patientCount: string
  }
}

export function priceTemplate(
  name: PricingTemplateName,
  part: 'total' | 'dollars' | 'cents' = 'total',
  currency = true,
): string {
  return `${currency ? '$' : ''}{{pricing.${name}.${part}}}`
}

// Fallback prices, mirroring the live prices at the time of writing. Used
// only when the pricing API is unavailable, so content never renders with
// empty price fragments.
const defaultPricingCentsByKey: Record<PricingKey, number> = {
  MEDICAL_CERTIFICATE_SINGLE_DAY: 1990,
  MEDICAL_CERTIFICATE_MULTI_DAY: 2990,
  PRESCRIPTION: 2490,
  SPECIALIST_REFERRAL: 4990,
  GENERAL_CONSULTATION: 4990,
  TREATMENT_INITIAL_CONSULTATION: 4990,
  WEIGHT_LOSS_INITIAL_CONSULTATION: 8990,
  WEIGHT_LOSS_INITIAL_ASSESSMENT: 4000,
  WEIGHT_LOSS_MONTHLY_PLAN: 4990,
}

export const defaultCmsTemplateData: CmsTemplateData = {
  pricing: defaultTemplateValues(),
  socialProof: socialProofTemplateValues(),
}

const PRICING_REVALIDATE_SECONDS = 300

type LoadedTemplateData = {
  data: CmsTemplateData
  fromApi: boolean
}

let browserTemplateData: Promise<CmsTemplateData> | undefined

export async function fetchCmsTemplateData(): Promise<CmsTemplateData> {
  if (typeof window === 'undefined') {
    const loaded = await loadTemplateData(process.env.API_DOMAIN)
    return loaded.data
  }

  if (!browserTemplateData) {
    const loading = loadTemplateData(ClientConfig.apiUrl)
    browserTemplateData = loading.then((loaded) => loaded.data)
    void loading.then((loaded) => {
      // Keep retrying on later fetches while we are serving fallback prices.
      if (!loaded.fromApi) {
        browserTemplateData = undefined
      }
    })
  }

  return browserTemplateData
}

async function loadTemplateData(
  apiBaseUrl: string | undefined,
): Promise<LoadedTemplateData> {
  if (!apiBaseUrl) {
    return { data: defaultCmsTemplateData, fromApi: false }
  }

  try {
    const response = await fetch(`${apiBaseUrl}/pricing`, {
      next: { revalidate: PRICING_REVALIDATE_SECONDS, tags: ['pricing'] },
    })
    if (!response.ok) {
      throw new Error(`GET /pricing responded with ${response.status}`)
    }

    const pricing = (await response.json()) as PricingResponse
    return {
      data: {
        pricing: toPriceTemplateValues(pricing),
        socialProof: socialProofTemplateValues(),
      },
      fromApi: true,
    }
  } catch (error) {
    console.warn(
      'Pricing templating could not load pricing data; using default prices',
      error,
    )
    return { data: defaultCmsTemplateData, fromApi: false }
  }
}

function toPriceTemplateValues(
  response: PricingResponse,
): CmsTemplateData['pricing'] {
  // Defaults fill any price the backend could not load from Stripe.
  const values = defaultTemplateValues()

  for (const [key, price] of Object.entries(response.prices ?? {})) {
    const templateName = templateNameByPricingKey[key as PricingKey]
    if (!templateName || typeof price?.amountCents !== 'number') continue

    values[templateName] = toPriceTemplateValue(price.amountCents)
  }

  return withSpecialistReferralPricing(values)
}

function defaultTemplateValues(): CmsTemplateData['pricing'] {
  const values = {} as CmsTemplateData['pricing']

  for (const [key, amountCents] of Object.entries(defaultPricingCentsByKey)) {
    values[templateNameByPricingKey[key as PricingKey]] =
      toPriceTemplateValue(amountCents)
  }

  return withSpecialistReferralPricing(values)
}

function withSpecialistReferralPricing(
  values: CmsTemplateData['pricing'],
): CmsTemplateData['pricing'] {
  return {
    ...values,
    // Specialist referral requests now use the general consultation pathway.
    specialistReferral: values.generalConsultation,
  }
}

function toPriceTemplateValue(amountCents: number): PriceTemplateValue {
  const dollars = Math.floor(amountCents / 100).toString()
  const cents = (amountCents % 100).toString().padStart(2, '0')

  return {
    total: `${dollars}.${cents}`,
    dollars,
    cents,
  }
}

function socialProofTemplateValues(): CmsTemplateData['socialProof'] {
  return {
    rating: RATINGS.stars.toFixed(1),
    reviewCount: new Intl.NumberFormat('en-AU').format(RATINGS.reviews),
    patientCount: new Intl.NumberFormat('en-AU').format(RATINGS.patients),
  }
}
